import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, XCircle, Clock, Home, CalendarPlus, Phone } from 'lucide-react';
import { getPaymentStatus } from '../api';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const noPayment = searchParams.get('no_payment');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    // No-payment flow: booking submitted without deposit
    if (noPayment) {
      setLoading(false);
      return;
    }

    if (!sessionId) {
      setLoading(false);
      return;
    }

    const pollStatus = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setStatus(data);
        if (data.payment_status === 'paid' || data.status === 'expired') {
          setLoading(false);
          return;
        }
        if (pollAttempts < maxAttempts) {
          setPollAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error polling payment status:', err);
        setLoading(false);
      }
    };

    pollStatus();
  }, [sessionId, noPayment]);

  // No-payment booking confirmation
  if (noPayment) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6" data-testid="booking-success-no-payment">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-black mb-2">Booking Submitted!</h1>
          <p className="text-gray-600 mb-4">Your reservation request has been received.</p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left space-y-2">
            <p className="text-green-800 text-sm font-semibold">What happens next?</p>
            <ul className="text-green-700 text-xs space-y-1.5">
              <li>1. Our team will review your booking within 1 business day</li>
              <li>2. You'll receive a confirmation call or email</li>
              <li>3. If a deposit is required, we'll send you a secure payment link</li>
              <li>4. Your vehicle & driver will be confirmed</li>
            </ul>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
            <p className="text-xs text-gray-500 mb-1">Questions? Call ACE directly:</p>
            <a href="tel:6788463009" className="flex items-center justify-center text-sm font-semibold text-red-600 hover:text-red-700">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>

          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/')} variant="outline" className="gap-2" data-testid="home-btn">
              <Home className="h-4 w-4" /> Home
            </Button>
            <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700" data-testid="book-another-btn">
              Book Another
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-black mb-2">Invalid Session</h1>
          <p className="text-gray-600 mb-6">No payment session found.</p>
          <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700">
            Back to Booking
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-black mb-2">Processing Payment</h1>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </Card>
      </div>
    );
  }

  const isPaid = status?.payment_status === 'paid';
  const isExpired = status?.status === 'expired';

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 text-center">
        {isPaid ? (
          <>
            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-black mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-4">Your deposit of ${status?.amount?.toFixed(2)} has been received.</p>

            {status?.booking_id && (
              <p className="text-sm text-gray-500 mb-6">
                Booking Reference: <span className="font-mono font-bold">{status.booking_id}</span>
              </p>
            )}

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                You will receive a confirmation email shortly with your booking details.
                Our team will contact you to finalize arrangements.
              </p>
            </div>

            {status?.booking_id && (
              <Button
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/booking/${status.booking_id}/ics`, '_blank')}
                variant="outline"
                className="w-full mb-4 border-green-300 text-green-700 hover:bg-green-50 gap-2"
                data-testid="add-to-calendar-btn"
              >
                <CalendarPlus className="h-4 w-4" />
                Add to Calendar
              </Button>
            )}
          </>
        ) : isExpired ? (
          <>
            <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-black mb-2">Session Expired</h1>
            <p className="text-gray-600 mb-6">Your payment session has expired. Please try booking again.</p>
          </>
        ) : (
          <>
            <div className="h-20 w-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="h-12 w-12 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-bold text-black mb-2">Payment Pending</h1>
            <p className="text-gray-600 mb-6">Your payment is being processed. Please check back shortly.</p>
          </>
        )}

        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate('/')} variant="outline" className="gap-2">
            <Home className="h-4 w-4" /> Home
          </Button>
          {!isPaid && (
            <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700">
              Try Again
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BookingSuccess;
